<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { computed, ref } from "vue"

import InputError from "@/components/InputError.vue"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { getAvatarColor, getInitials } from "@/composables/useInitials"
import AppLayout from "@/layouts/AppLayout.vue"
import { editUserPath, userPath, usersPath } from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface User {
  id: number
  name: string
  email: string
  active: boolean
  roles: string[]
}

const props = defineProps<{ user: User }>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Staff", href: usersPath() },
  { title: props.user.name, href: editUserPath(props.user.id) },
]

const form = useForm({
  name:   props.user.name,
  email:  props.user.email,
  active: props.user.active,
  role:   props.user.roles[0] ?? "",
})

const saved = ref(false)

const userInitials = computed(() => getInitials(props.user.name))
const userAvatarColor = computed(() => getAvatarColor(props.user.name))

function submit() {
  form.patch(userPath(props.user.id), {
    preserveScroll: true,
    onSuccess: () => {
      saved.value = true
      setTimeout(() => { saved.value = false }, 2500)
    },
  })
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <Head :title="user.name" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <Avatar class="size-10">
          <AvatarFallback class="font-semibold" :class="userAvatarColor">{{ userInitials }}</AvatarFallback>
        </Avatar>
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-foreground text-xl font-semibold">{{ user.name }}</h1>
            <Badge :variant="user.active ? 'default' : 'secondary'">
              {{ user.active ? "Active" : "Inactive" }}
            </Badge>
          </div>
          <p class="text-muted-foreground text-sm">{{ user.email }}</p>
        </div>
      </div>

      <!-- Edit form -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border max-w-lg">
        <CardHeader>
          <CardTitle class="text-base">Account Details</CardTitle>
          <CardDescription>Update this user's name, email, role, and status.</CardDescription>
        </CardHeader>
        <CardContent>
          <form class="space-y-5" @submit.prevent="submit">
            <div class="grid gap-2">
              <Label for="name">Name</Label>
              <Input id="name" v-model="form.name" placeholder="Full name" required />
              <InputError :messages="err('name')" />
            </div>

            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="form.email" type="email" placeholder="email@example.com" required />
              <InputError :messages="err('email')" />
            </div>

            <div class="grid gap-2">
              <Label for="role">Role</Label>
              <Select v-model="form.role">
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="associate">Associate</SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="err('role')" />
            </div>

            <div class="flex items-center gap-3">
              <Switch id="active" v-model="form.active" />
              <Label for="active">Active</Label>
            </div>

            <div class="flex items-center gap-3 pt-1">
              <Button type="submit" :disabled="form.processing">Save Changes</Button>
              <Transition
                enter-from-class="opacity-0"
                enter-active-class="transition duration-200"
                leave-active-class="transition duration-500"
                leave-to-class="opacity-0"
              >
                <span v-if="saved" class="text-muted-foreground text-sm">Saved</span>
              </Transition>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
